import {useEffect, useState, ReactNode, FC} from 'react'
import classNames from 'classnames'

import {RenderIf, CopyButtonWithText} from 'components'

export type SnackbarVariant = 'success' | 'error' | 'warning' | 'info'
export type SnackbarPosition =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'top-middle'
  | 'bottom-middle'

export type SnackbarProps = {
  /** This is the text that is displayed in the component*/
  text?: string
  children?: ReactNode
  variant?: SnackbarVariant
  position?: SnackbarPosition
  show?: boolean
  onClose?: () => void
  onCopy?: () => void
  autoHideDuration?: number
  copyText?: string
  showCopy?: boolean
  isCopyLoading: boolean
  copyButtonText?: string
  dismissible?: boolean
  className?: string
  dataCy?: string
}

const variantClasses: Record<
  SnackbarVariant,
  {container: string; border: string}
> = {
  success: {
    container: 'bg-green-100/45',
    border: 'border-green-100',
  },
  error: {
    container: 'bg-red-100/45',
    border: 'border-red-100',
  },
  warning: {
    container: 'bg-yellow-200',
    border: 'border-yellow-100',
  },
  info: {
    container: 'bg-dope-cyan-500/30',
    border: 'border-dope-cyan-100',
  },
}

const positionClasses: Record<SnackbarPosition, string> = {
  'top-left': 'top-4 left-4',
  'top-right': 'top-4 right-4',
  'bottom-left': 'bottom-4 left-4',
  'bottom-right': 'bottom-4 right-4',
  'top-middle': 'top-4 left-1/2 -translate-x-1/2',
  'bottom-middle': 'bottom-4 left-1/2 -translate-x-1/2',
}

const Snackbar: FC<SnackbarProps> = ({
  text,
  children,
  variant = 'info',
  position = 'bottom-right',
  show = false,
  onClose,
  onCopy,
  autoHideDuration,
  copyText,
  showCopy = false,
  isCopyLoading,
  copyButtonText = 'Copy',
  dismissible = true,
  className,
  dataCy = 'snackbar',
}) => {
  const [open, setOpen] = useState(show)

  useEffect(() => {
    setOpen(show)
  }, [show])

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined

    if (open && autoHideDuration) {
      timeoutId = setTimeout(() => {
        handleClose()
      }, autoHideDuration)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [open, autoHideDuration])

  const handleClose = () => {
    setOpen(false)
    onClose && onClose()
  }

  if (!open) return null

  const {container, border} = variantClasses[variant]

  return (
    <div
      role="status"
      data-cy={dataCy}
      className={classNames(
        'fixed z-100 px-4 py-3 min-w-64 max-w-102 min-h-28 rounded border-1 backdrop-blur-xl animate-slideIn',
        'flex flex-col items-end gap-4',
        container,
        border,
        positionClasses[position],
        className,
      )}
    >
      <div className="flex-1 text-sm karla mr-2">
        {children || text || 'Notification'}
      </div>
      <div className="flex flex-col items-end gap-2 relative">
        <RenderIf isTrue={showCopy}>
          <CopyButtonWithText
            copyText={copyText}
            copyButtonText={copyButtonText}
            dataCy="snackbar-copy-button"
            isLoading={isCopyLoading}
            onCopy={onCopy}
          />
        </RenderIf>
      </div>
      <RenderIf isTrue={dismissible}>
        <button
          aria-label="Close notification"
          onClick={handleClose}
          className="text-gray-500 hover:text-gray-300 transition-colors text-sm absolute top-1.5 right-3"
        >
          ✕
        </button>
      </RenderIf>
    </div>
  )
}

export default Snackbar
